import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RequiredServicesTable = ({ deploymentPoints, onExport, onViewDetails }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterSector, setFilterSector] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const sectorOptions = [
    { value: '', label: 'All Sectors' },
    { value: 'registration', label: 'Registration' },
    { value: 'kitchen', label: 'Kitchen Service' },
    { value: 'security', label: 'Security' },
    { value: 'medical', label: 'Medical' },
    { value: 'transport', label: 'Transport' },
    { value: 'cleaning', label: 'Cleaning' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'critical', label: 'Critical Shortage' },
    { value: 'partial', label: 'Partially Filled' },
    { value: 'complete', label: 'Fully Allocated' }
  ];

  const getStatusInfo = (required, allocated) => {
    const percentage = (allocated / required) * 100;
    if (percentage >= 100 || (allocated == required)) {
      return { status: 'complete', color: 'success', label: 'Fully Allocated' };
    } else if (percentage >= 70) {
      return { status: 'partial', color: 'warning', label: 'Partially Filled' };
    } else {
      return { status: 'critical', color: 'error', label: 'Critical Shortage' };
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = deploymentPoints.filter(point => {
      const matchesSearch = point.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           point.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSector = !filterSector || point.sector === filterSector;
      const statusInfo = getStatusInfo(point.reqCount, point.allocatedCount);
      const matchesStatus = !filterStatus || statusInfo.status === filterStatus;
      
      return matchesSearch && matchesSector && matchesStatus;
    });

    filtered = filtered.filter(point => point.reqCount > 0);

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [deploymentPoints, searchQuery, sortField, sortDirection, filterSector, filterStatus]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleRowExpansion = (pointId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(pointId)) {
      newExpanded.delete(pointId);
    } else {
      newExpanded.add(pointId);
    }
    setExpandedRows(newExpanded);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ArrowUpDown';
    return sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getStatusBadge = (required, allocated) => {
    const statusInfo = getStatusInfo(required, allocated);
    const colorClasses = {
      success: 'bg-success/10 text-success border-success/20',
      warning: 'bg-warning/10 text-warning border-warning/20',
      error: 'bg-error/10 text-error border-error/20'
    };

    return (
      <span className={`
        px-2 py-1 text-xs font-caption rounded-full border bg-white
        ${colorClasses[statusInfo.color]}
      `}>
        {statusInfo.label}
      </span>
    );
  };

  const getProgressBar = (required, allocated) => {
    const percentage = Math.min((allocated / required) * 100, 100);
    const statusInfo = getStatusInfo(required, allocated);
    
    const colorClasses = {
      success: 'bg-success',
      warning: 'bg-warning',
      error: 'bg-error'
    };

    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${colorClasses[statusInfo.color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="md:col-span-2">
            <Input
              type="search"
              placeholder="Search deployment points..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select
            placeholder="Filter by Status"
            options={statusOptions}
            value={filterStatus}
            onChange={setFilterStatus}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {filteredAndSortedData.length} of {deploymentPoints.length} deployment points
          </div>
          
          {/* <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('excel')}
              iconName="Download"
              iconPosition="left"
            >
              Export Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('pdf')}
              iconName="FileText"
              iconPosition="left"
            >
              Export PDF
            </Button>
          </div> */}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <div className="table w-full sector-table">
            <div className="table-header-group bg-muted/50">
              <div className="table-row">
                <div className="table-cell text-left md:align-middle p-4 font-heading font-medium text-foreground">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center space-x-1 hover:text-primary transition-colors"
                  >
                    <span>Deployment Point</span>
                    <Icon name={getSortIcon('name')} size={14} />
                  </button>
                </div>
                <div className="table-cell text-left md:align-middle p-4 font-heading font-medium text-foreground">
                  Description
                </div>
                <div className="table-cell text-center md:align-middle p-4 font-heading font-medium text-foreground">
                  <button
                    onClick={() => handleSort('requiredCount')}
                    className="flex items-center space-x-1 hover:text-primary transition-colors mx-auto"
                  >
                    <span>Required</span>
                    <Icon name={getSortIcon('requiredCount')} size={14} />
                  </button>
                </div>
                <div className="table-cell text-center md:align-middle p-4 font-heading font-medium text-foreground">
                  <button
                    onClick={() => handleSort('allocatedCount')}
                    className="flex items-center space-x-1 hover:text-primary transition-colors mx-auto"
                  >
                    <span>Allocated</span>
                    <Icon name={getSortIcon('allocatedCount')} size={14} />
                  </button>
                </div>
                <div className="table-cell text-center md:align-middle p-4 font-heading font-medium text-foreground">
                  Status
                </div>
                {/* <div className="table-cell text-center md:align-middle p-4 font-heading font-medium text-foreground">
                  Actions
                </div> */}
              </div>
            </div>
            <div className="table-row-group">
              {filteredAndSortedData.map((point, index) => (
                <React.Fragment key={point._id}>
                  <div className={`
                    border-b border-border hover:bg-muted/30 transition-colors table-row px-1 hover:bg-muted/30
                    ${index % 2 === 0 ? 'zebra-stripe' : ''}
                  `}>
                    <div className="table-cell p-4 mt-2 md:mt-0 md:align-middle md:before:content-[] before:content-[attr(data-title)] font-semibold">
                      <div className="flex items-center space-x-2">
                        {/* <button
                          onClick={() => toggleRowExpansion(point._id)}
                          className="text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Icon 
                            name={expandedRows.has(point.id) ? 'ChevronDown' : 'ChevronRight'} 
                            size={16} 
                          />
                        </button> */}
                        <div>
                          <div className="font-heading md:font-medium font-bold text-foreground mt-1">
                            {point.name}
                          </div>
                          {/* <div className="text-xs text-muted-foreground font-caption">
                            {point.sector.charAt(0).toUpperCase() + point.sector.slice(1)} Sector
                          </div> */}
                        </div>
                      </div>
                    </div>
                    <div className="table-cell p-4 md:mt-0 md:align-middle md:before:content-[] before:content-[attr(data-title)] font-semibold" >
                      <div className="text-sm text-muted-foreground">
                        {point.description}
                      </div>
                    </div>
                    <div className="table-cell p-4 mt-2 md:mt-0 md:text-center md:align-middle md:before:content-[] before:content-[attr(data-title)] font-semibold before:text-gray-500" data-title="Required:">
                      <span className="font-heading font-semibold text-foreground ml-3 md:ml-0">
                        {point.reqCount}
                      </span>
                    </div>
                    <div className="table-cell p-4 mt-2 md:mt-0 md:text-center md:align-middle md:before:content-[] before:content-[attr(data-title)] font-semibold before:text-gray-500" data-title="Allocated:">
                      <div className="space-y-1 ml-3 md:ml-0 inline-flex gap-3 md:block md:g-0">
                        <span className="font-heading font-semibold text-foreground">
                          {point.allocatedCount}
                        </span>
                        <div className="min-w-16 mx-auto">
                          {getProgressBar(point.reqCount, point.allocatedCount)}
                        </div>
                      </div>
                    </div>
                    <div className="table-cell p-4 mr-1 md:mr-0 mt-3 md:mt-0 md:align-middle !w-auto whitespace-nowrap" data-title="Status">
                      {getStatusBadge(point.reqCount, point.allocatedCount)}
                    </div>
                    {/* <div className="table-cell p-4 mt-2 md:mt-0 md:align-middle" data-title="Actions">
                      <div className="flex items-center justify-center space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          iconSize="16"
                          onClick={() => onViewDetails(point)}
                          iconName="Eye"
                          iconPosition="left"
                          >
                          <span className='inline-block md:hidden lg:inline-block'>Preview</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconSize="16"
                          onClick={() => console.log('Edit deployment point:', point.id)}
                          iconName="Edit"
                          iconPosition="left"
                          >
                            <span className='inline-block md:hidden lg:inline-block'>Edit</span>
                        </Button>
                        {/* <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewDetails(point)}
                          iconName="Eye"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => console.log('Edit deployment point:', point.id)}
                          iconName="Edit"
                        /> */}
                      {/* </div>
                    </div> */} 
                  </div>
                  
                  {/* Expanded Row Details */}
                  {expandedRows.has(point.id) && (
                    <tr className="bg-muted/20 border-b border-border">
                      <td colSpan="6" className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <h4 className="font-heading font-medium text-foreground mb-2">
                              Leadership
                            </h4>
                            <div className="space-y-1 text-muted-foreground">
                              <p>Leader 1: {point.leader1 || 'Not Assigned'}</p>
                              <p>Leader 2: {point.leader2 || 'Not Assigned'}</p>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-heading font-medium text-foreground mb-2">
                              Requirements
                            </h4>
                            <div className="space-y-1 text-muted-foreground">
                              <p>Shortage: {point.reqCount - point.allocatedCount} members</p>
                              <p>Fill Rate: {Math.round((point.allocatedCount / point.reqCount) * 100)}%</p>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-heading font-medium text-foreground mb-2">
                              Skills Required
                            </h4>
                            <div className="flex flex-wrap gap-1">
                              {point.skillsRequired?.map((skill, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                                >
                                  {skill}
                                </span>
                              )) || <span className="text-muted-foreground">No specific skills</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {filteredAndSortedData.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-medium text-foreground mb-2">
              No deployment points found
            </h3>
            <p className="text-muted-foreground">
              Try adjusting your search criteria or filters
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequiredServicesTable;
